import axios from "axios";
import { currentTenantSlug, tenantBase, storageKey } from "@/config/host";

// Base URL of the PHP REST API. In the multi-tenant SaaS each school is served
// from its own subdomain (greenwood.skulkit.com), and its API lives on that same
// host — so we derive the base from window.location, keeping every request on the
// tenant's own origin (Host header + JWT are all the backend needs to scope it).
// VUE_APP_API_URL still overrides everything for local dev, where the API is at a
// fixed XAMPP path (http://localhost/school-kit/api) separate from the dev server.
// In production the backend lives under a path on the same host (e.g. /backend/api
// on Hostinger), so VUE_APP_API_PATH sets that prefix while the host stays the
// tenant's own subdomain — defaulting to /api when unset.
// In a production build always speak HTTPS (avoids mixed-content if the page was
// somehow reached over http before the server redirect); in dev mirror the page.
const protocol =
  process.env.NODE_ENV === "production" ? "https:" : window.location.protocol;
const baseURL =
  process.env.VUE_APP_API_URL ||
  `${protocol}//${window.location.host}${process.env.VUE_APP_API_PATH || "/api"}`;

const client = axios.create({ baseURL });

// Attach the JWT + tenant to every request. Tenancy is path-based, so the API
// host is always the apex (skulkit.com/backend/api) and can't identify the
// school — we pass the slug explicitly. The X-Tenant header covers same-origin
// production; the ?tenant= query mirror covers local dev, where the API is a
// separate XAMPP origin (VUE_APP_API_URL) that the SPA path never reaches. The
// backend re-pins this against the JWT's school_id, so a spoofed header 403s.
client.interceptors.request.use((config) => {
  const token = localStorage.getItem(storageKey("token"));
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenant = currentTenantSlug();
  if (tenant) {
    config.headers["X-Tenant"] = tenant;
    config.params = { ...(config.params || {}), tenant };
  }
  return config;
});

// On 401, clear the session and bounce to login (unless already an auth call).
client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";
    // The staff-attendance kiosk is a public (unauthenticated) page: a bad
    // passcode or fingerprint legitimately 401s and must NOT bounce the device
    // to login. Covers the passcode `mark` and the WebAuthn `auth-options`/`auth`
    // ceremonies (but not the authed `webauthn/register*` admin endpoints).
    if (
      status === 401 &&
      !url.includes("/auth/login") &&
      !url.includes("/staff-attendance/mark") &&
      !url.includes("/staff-attendance/webauthn/auth")
    ) {
      localStorage.removeItem(storageKey("token"));
      localStorage.removeItem(storageKey("user"));
      // Bounce to this tenant's login (base already carries /school/{slug}/).
      const loginPath = `${tenantBase()}login`.replace(/\/{2,}/g, "/");
      if (window.location.pathname !== loginPath) {
        window.location.assign(loginPath);
      }
    }
    return Promise.reject(error);
  },
);

export default client;
